import { Dispatch, useCallback } from 'react'

import {
  SubmitFormRequestElementResult,
  SubmitFormResponse,
} from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import {
  BlockerAction,
  BlockerActionEndFlowAction,
  BlockerActionEndFlowActionResult,
} from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import logger from '@cash-web/shared/util-logger'
import { useLogout } from '@cash-web/shared-auth-feature-main'
import { useMutationEndFlow } from '@cash-web/shared-data-access-api/react-query/useMutationEndFlow'
import { useMutationSubmitForm } from '@cash-web/shared-data-access-api/react-query/useMutationSubmitForm'
import { useLoadUrl } from '@cash-web/shared-data-access-load-url'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { FlowContextProps, LoadUrl } from '@cash-web/shared-flow-data-access-context'
import {
  BlockerActionDataDetail,
  BlockerActionKey,
  FlowUpdateProps,
  SkipBlocker,
} from '@cash-web/shared-flow-data-access-context'
import { JourneyContextProps, useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { OptionalMiddlewareContextProps } from '@cash-web/shared-util-fetch'
import { getRouteInfo } from '@cash-web/shared-util-route-info'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { FlowReducerAction, FlowState } from './flowReducer'
import { useFlowLogging } from './useFlowLogging'

export interface ExtendedBlockerActionEndFlowAction extends BlockerActionEndFlowAction {
  onCancelFlow?: () => void
}
export interface ExtendedBlockerAction extends BlockerAction {
  end_flow_action?: ExtendedBlockerActionEndFlowAction
}

export const getBlockerActionPair = (action: ExtendedBlockerAction): BlockerActionDataDetail => {
  const key = getBlockerActionKey(action)
  return { key, action: action[key] } as BlockerActionDataDetail
}

export const getBlockerActionKey = (action: ExtendedBlockerAction) => {
  const { text, localized_text, ...actions } = action

  const actionKeys = Object.keys(actions) as BlockerActionKey[]
  // we need to have pre-actions because in some cases we get multiple actions
  // for example confirmation_dialog, might be passed with another action to be performed after confirmation
  const preAction: BlockerActionKey | undefined = actionKeys.find(key =>
    ['confirmation_dialog', 'confirmation_sheet'].includes(key)
  )
  // grab the first action in the object
  const firstAction = Object.keys(actions)[0] as BlockerActionKey
  // should be only one action
  return preAction ?? firstAction
}

export const getBlockerAction = (
  action: ExtendedBlockerAction
): {
  isSupported: boolean
  actionName?: BlockerActionKey
  execute?: ({
    completeFlow,
    cancelFlow,
    dispatch,
    endFlow,
    updateFlow,
    logout,
    results,
    skipBlocker,
    submitForm,
    journey,
    loadUrl,
    logCdfEvent,
  }: {
    completeFlow: () => void
    cancelFlow: FlowContextProps['cancel']
    updateFlow: (data: FlowUpdateProps) => Promise<void>
    dispatch: Dispatch<FlowReducerAction>
    endFlow: ReturnType<typeof useMutationEndFlow>['mutateAsync']
    logout: ReturnType<typeof useLogout>
    results: SubmitFormRequestElementResult[] | undefined
    skipBlocker: SkipBlocker
    submitForm: ReturnType<typeof useMutationSubmitForm>['mutateAsync']
    journey: JourneyContextProps
    loadUrl: LoadUrl
    logCdfEvent: ReturnType<typeof useLogCashWebCdfEvent>
  }) => void | Promise<void> | Promise<SubmitFormResponse>
} => {
  if (!action) {
    return {
      isSupported: false,
    }
  }
  const actionName = getBlockerActionKey(action)
  const { text } = action

  if (!actionName) {
    return {
      isSupported: false,
    }
  }

  switch (actionName) {
    case 'copy_action': {
      return {
        isSupported: true,
        actionName,
        execute: () => {
          const copyText = action?.copy_action?.text
          if (copyText) {
            navigator.clipboard.writeText(copyText)
          }
        },
      }
    }

    case 'confirmation_dialog': {
      return {
        isSupported: true,
        actionName,
        execute: ({ dispatch }) => {
          dispatch({
            type: 'BLOCKER_ACTION_VIEW',
            blockerActionView: action,
          })
        },
      }
    }
    case 'end_activity_action': {
      return {
        isSupported: true,
        actionName,
        execute: ({ completeFlow }) => {
          // This is an Android-only action that is unexpected on web. Alert Bugsnag if received from server.
          logger.error(new Error(`Blocker - end_activity_action is unexpected`))
          // Complete the flow to keep the app functional for the customer.
          completeFlow()
        },
      }
    }
    case 'end_flow_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ completeFlow, cancelFlow, endFlow }) => {
          const actionId = action?.end_flow_action?.notify_end_flow_id
          if (actionId) {
            await endFlow({ action_id: actionId })
          }

          if (action?.end_flow_action?.result === BlockerActionEndFlowActionResult.CANCELLED) {
            // force cancel the flow, and ignore any dismiss actions
            cancelFlow({ force: true, onCancelFlowOverride: action?.end_flow_action?.onCancelFlow })
          } else {
            completeFlow()
          }
        },
      }
    }
    case 'internal_navigation_action': {
      const url = action?.internal_navigation_action?.client_route?.url
      const routeInfo = url ? getRouteInfo(url) : undefined
      const supportFlowNode = action?.internal_navigation_action?.support?.support_flow_node
      const isPreferred = action?.internal_navigation_action?.necessity === 'PREFERRED'
      const isUnknownUnsupported = routeInfo?.type === 'unsupported'
      const isKnownUnsupported = routeInfo?.type === 'known-unsupported'
      const hasSupportFlowNode = supportFlowNode != null
      const isSupported = (routeInfo && !isKnownUnsupported && !isUnknownUnsupported) || hasSupportFlowNode

      if ((!routeInfo || isUnknownUnsupported) && !hasSupportFlowNode) {
        WebViewUnsupportedFeature({
          source: 'getBlockerAction',
          feature: 'BLOCKER_ACTION',
          action: `internal_navigation_action.${supportFlowNode ? 'support' : 'client_route'}`,
          context: url,
        })
      }

      return {
        isSupported,
        actionName,
        execute: async ({ loadUrl, journey }) => {
          if (url != null) {
            const isMoneybot = journey.hasParentTarget('outlet:moneybot')
            if (isMoneybot && isPreferred) {
              journey.complete()
              return
            }
            await loadUrl({ trackingName: text, url, options: { replace: true } })
          } else if (supportFlowNode != null) {
            window.open(`/support/${supportFlowNode}`, '_blank')
          }
        },
      }
    }
    case 'open_url_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ completeFlow, loadUrl }) => {
          await loadUrl({ trackingName: text, url: action?.open_url_action?.url })

          if (action?.open_url_action?.end_flow) {
            completeFlow()
          }
        },
      }
    }
    case 'sign_out_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ logout }) => {
          await logout({ returnToSameUrl: false })
        },
      }
    }
    case 'share_action': {
      return {
        // currently there is no way to share a pdf file on web
        isSupported: action.share_action?.extension !== 'pdf',
        actionName,
        execute: () => {
          const option = action?.share_action?.options?.[0]
          if (option?.email) {
            const { body, subject } = option.email
            window.open(`mailto:?subject=${subject}&body=${body}`)
          }
        },
      }
    }
    case 'skip_blocker_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ skipBlocker }) => {
          await skipBlocker()
        },
      }
    }
    case 'submit_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ results, submitForm, updateFlow }) => {
          const result = await submitForm({ action_id: action?.submit_action?.id, results })
          //TODO: update this so we do not require the results to be returned to the caller (HelpMenu)
          updateFlow({
            response: Promise.resolve(result),
            queriesToInvalidate: [],
          })
          return result
        },
      }
    }
    case 'view_action': {
      return {
        isSupported: true,
        actionName,
        execute: async ({ loadUrl }) => {
          await loadUrl({ trackingName: text, url: action?.view_action?.file_url })
        },
      }
    }

    case 'back_navigation_action': {
      return {
        isSupported: Boolean(action?.back_navigation_action?.redirect_url), // prompt backnavs are not supported yet
        actionName,
        execute: async ({ loadUrl }) => {
          await loadUrl({
            trackingName: text,
            url: action?.back_navigation_action?.redirect_url,
            target: '_self',
          })
        },
      }
    }
    // need to support these in future
    case 'share_text_action':
    case 'menu_action':
    case 'confirmation_sheet':
    case 'dialog_action':
    default: {
      return {
        isSupported: false,
        actionName,
        execute: ({ logCdfEvent }) => {
          logCdfEvent(
            WebViewUnsupportedFeature({
              source: 'useBlockerAction.execute',
              feature: 'BLOCKER_ACTION',
              action: actionName,
            })
          )
        },
      }
    }
  }
}

export type ExecuteBlockerAction = (
  action: BlockerAction,
  results?: SubmitFormRequestElementResult[]
) => Promise<void | SubmitFormResponse>
export const useBlockerAction = ({
  state,
  dispatch,
  completeFlow,
  cancelFlow,
  skipBlocker,
  updateFlow,
  optionalMiddleware,
}: {
  state: FlowState
  dispatch: Dispatch<FlowReducerAction>
  cancelFlow: FlowContextProps['cancel']
  completeFlow: () => void
  updateFlow: (data: FlowUpdateProps) => Promise<void>
  skipBlocker: SkipBlocker
  optionalMiddleware: OptionalMiddlewareContextProps
}) => {
  const { mutateAsync: submitForm } = useMutationSubmitForm({ options: { optionalMiddleware } })
  const { mutateAsync: endFlow } = useMutationEndFlow({ options: { optionalMiddleware } })
  const { logExecuteBlockerAction } = useFlowLogging()
  const logCdfEvent = useLogCashWebCdfEvent()
  const logout = useLogout()
  const loadUrl = useLoadUrl()

  const journey = useJourneyContext()

  const executeBlockerAction: ExecuteBlockerAction = useCallback(
    async (action, results) => {
      const { text } = action

      logExecuteBlockerAction({ state, action, trackingName: text })

      dispatch({ type: 'BLOCKER_ACTION_EXECUTE', blockerAction: action, trackingName: text })

      const blockerAction = getBlockerAction(action)
      const blockerActionReturnValue = await blockerAction.execute?.({
        completeFlow,
        cancelFlow,
        updateFlow,
        dispatch,
        endFlow,
        loadUrl,
        logout,
        results,
        skipBlocker,
        submitForm,
        journey,
        logCdfEvent,
      })

      return blockerActionReturnValue ? blockerActionReturnValue : undefined
    },
    [
      logExecuteBlockerAction,
      state,
      dispatch,
      completeFlow,
      cancelFlow,
      updateFlow,
      endFlow,
      loadUrl,
      logout,
      skipBlocker,
      submitForm,
      journey,
      logCdfEvent,
    ]
  )
  return executeBlockerAction
}
